import { makeUniqueId } from '@apollo/client/utilities'
import { Add } from '@mui/icons-material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Box,
    Button,
    CircularProgress,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material'
import {
    DataGridPro,
    GridFooterContainer,
    GridPagination,
    GridPreferencePanelsValue,
    GridRowSelectionModel,
    useGridApiContext,
    useGridApiRef,
    type DataGridProProps,
    type GridColDef,
    type GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro'
import {
    RosterStatus,
    type GetFilteredRostersSansCandidateQuery,
    type SortField,
    SortIndexField,
} from '__generated__/graphql'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import RosterStatusPill from 'recruiter/components/RosterStatusPill'
import {
    GetFilteredRostersSansCandidate,
    GetFilteredRostersWithCandidate,
} from 'recruiter/contexts'
import { useRosterSearch } from 'recruiter/hooks'
import ProcessStatusEditWithAddToRoster from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditWithAddToRoster'
import { recruiterRouteConfig } from 'recruiter/routes'
import { CenteredBox } from 'shared/components/CenteredBox'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { getPxAsRem, makeSortModel } from 'shared/utils'
import AddToRosterPanel from './AddToRosterPanel'
import BulkAddToRosters from './BulkAddToRosters'

const TableFooter = () => {
    const apiRef = useGridApiContext()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length
    return (
        <GridFooterContainer sx={{ pl: 1 }}>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <GridPagination />
        </GridFooterContainer>
    )
}

export type CandidateRostersSansCandidateTableRowModel =
    GetFilteredRostersSansCandidateQuery['rosterSearch']['rosters'][number]

type ColumnDefinition =
    GridColDef<CandidateRostersSansCandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

export function CandidateRostersSansCandidateTable() {
    const {
        data,
        isLoading,
        totalItems,
        tableState,
        setTableState,
        selectedRosterIds,
        setSelectedRosterIds,
        selectAllEditableRosters,
        isSelectingAll,
        editableTotalCount,
    } = useRosterSearch()
    const { candidateId } = useParams()
    const gridApi = useGridApiRef()
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useLocalStorage<GridColumnVisibilityModel>(
            'candidate-rosters-not-on-columns',
            {}
        )
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

    const handlePaginationModelChange = (model: {
        page: number
        pageSize: number
    }) => {
        setTableState({ page: model.page + 1, limit: model.pageSize })
    }

    const handleSortModelChange: DataGridProProps['onSortModelChange'] = (
        model
    ) => {
        if (model.length === 0) {
            setTableState({ sort: [], page: 1 })
            return
        }

        const sortableColumns = columns.filter(
            (column) => (column.sortable ?? true) && column.sortIndexField
        )

        if (
            !model.some(({ field }) =>
                sortableColumns.some((column) => column.field === field)
            )
        ) {
            return
        }

        const updatedSortFields = model.reduce<SortField[]>(
            (sortFields, item) => {
                const column = sortableColumns.find(
                    (column) => item.field === column.field
                )
                if (column?.sortIndexField && item.sort) {
                    sortFields.push({
                        field: column.sortIndexField,
                        order: item.sort,
                    })
                }
                return sortFields
            },
            []
        )

        setTableState({ sort: updatedSortFields, page: 1 })
    }

    const getDetailPanelContent: DataGridProProps<CandidateRostersSansCandidateTableRowModel>['getDetailPanelContent'] =
        (params) => (
            <AddToRosterPanel data={params} candidateId={candidateId ?? ''} />
        )

    const getDetailPanelHeight: DataGridProProps<CandidateRostersSansCandidateTableRowModel>['getDetailPanelHeight'] =
        () => 'auto'

    const rows = (data ?? []) as CandidateRostersSansCandidateTableRowModel[]

    const handleRowSelectionModelChange = (
        newSelectionModel: GridRowSelectionModel
    ) => {
        const newIds = newSelectionModel
            .map((id) => {
                const row = rows.find((r) => r.roster?.id === id)
                return row?.permissions?.canEdit ? (id as string) : null
            })
            .filter((id): id is string => id !== null)
        setSelectedRosterIds(newIds)
    }

    const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleAddClose = () => {
        setAnchorEl(null)
    }

    const handleAddSuccess = () => {
        setSelectedRosterIds([])
        setAnchorEl(null)
    }

    const columns: ColumnDefinition[] = [
        {
            field: 'rosterName',
            headerName: 'Search',
            flex: 1,
            sortIndexField: SortIndexField.RosterName,
            renderCell: ({ row }) => (
                <MuiLink
                    onClick={() => setTableState({ filters: [] })}
                    component={Link}
                    to={`${recruiterRouteConfig.rosters.path}/${row.roster?.id}`}
                >
                    {row.roster?.rosterName}
                </MuiLink>
            ),
        },
        {
            field: 'company',
            headerName: 'Company',
            flex: 1,
            sortIndexField: SortIndexField.RosterCompany,
            renderCell: ({ row }) => (
                <MuiLink
                    onClick={() => setTableState({ filters: [] })}
                    component={Link}
                    to={`${recruiterRouteConfig.companies.path}/${row.roster?.company?.id}`}
                >
                    {row.roster?.company?.value}
                </MuiLink>
            ),
        },
        {
            field: 'processStatus',
            headerName: 'Process Status',
            width: 240,
            sortable: false,
            renderCell: (params) => (
                <ProcessStatusEditWithAddToRoster
                    {...params}
                    canEdit={params.row.permissions?.canEdit ?? false}
                    refetchQueries={[
                        GetFilteredRostersSansCandidate,
                        GetFilteredRostersWithCandidate,
                    ]}
                    candidateId={candidateId ?? ''}
                    rosterId={params.row.roster?.id}
                />
            ),
        },
        {
            field: 'rosterStatus',
            headerName: 'Search Status',
            flex: 1,
            sortable: true,
            sortIndexField: SortIndexField.RosterStatus,
            renderCell: ({ row }) => (
                <RosterStatusPill
                    status={row.roster?.rosterStatus ?? RosterStatus.Draft}
                />
            ),
        },
    ]

    const sortModel = makeSortModel(tableState.sort, columns)

    return (
        <Stack sx={{ height: '100%', overflow: 'hidden' }}>
            <Stack
                direction="row"
                alignItems="center"
                gap={getPxAsRem(8)}
                padding={getPxAsRem(16)}
            >
                <Typography noWrap fontSize="0.8125rem" color="text.secondary">
                    {isLoading
                        ? 'Fetching results...'
                        : `${totalItems} results`}
                </Typography>

                {selectedRosterIds.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            pl: 1.5,
                            ml: 0.5,
                        }}
                    >
                        {(editableTotalCount === null ||
                            selectedRosterIds.length < editableTotalCount) && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={selectAllEditableRosters}
                                disabled={isSelectingAll}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                }}
                            >
                                {isSelectingAll ? (
                                    <>
                                        <CircularProgress
                                            size={12}
                                            sx={{ mr: 0.5 }}
                                        />
                                        Selecting...
                                    </>
                                ) : (
                                    `Select all`
                                )}
                            </Button>
                        )}
                        {editableTotalCount !== null &&
                            selectedRosterIds.length >= editableTotalCount && (
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    All selected
                                </Typography>
                            )}
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setSelectedRosterIds([])}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                                color: 'text.secondary',
                            }}
                        >
                            Clear ({selectedRosterIds.length})
                        </Button>
                    </Stack>
                )}

                <Box marginLeft="auto">
                    <Button
                        onClick={handleAddClick}
                        variant="contained"
                        disabled={selectedRosterIds.length === 0}
                    >
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            <Add fontSize="small" />
                            <Typography variant="button">Add</Typography>
                        </Stack>
                    </Button>
                </Box>

                <BulkAddToRosters
                    isOpen={Boolean(anchorEl)}
                    onClose={handleAddClose}
                    onSuccess={handleAddSuccess}
                    anchorEl={anchorEl}
                    candidateId={candidateId ?? ''}
                    selectedRosterIds={selectedRosterIds}
                />
            </Stack>

            <DataGridPro
                apiRef={gridApi}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                checkboxSelection
                disableColumnFilter
                disableColumnMenu
                disableMultipleColumnsSorting
                disableRowSelectionOnClick
                rowSelectionModel={selectedRosterIds}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                isRowSelectable={(params) =>
                    params.row.permissions?.canEdit ?? false
                }
                keepNonExistentRowsSelected
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                loading={isLoading}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={totalItems}
                paginationModel={{
                    page: tableState.page - 1,
                    pageSize: tableState.limit,
                }}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[25, 50, 100]}
                getRowId={(row) =>
                    row.roster?.id ?? makeUniqueId('sansCandidatesTable')
                }
                rows={rows}
                slots={{
                    noRowsOverlay: getNoRowsOverlay,
                    detailPanelExpandIcon: ChevronRightIcon,
                    detailPanelCollapseIcon: ExpandMoreIcon,
                    footer: TableFooter,
                }}
            />
        </Stack>
    )
}

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No rosters found</Typography>
    </CenteredBox>
)
